import { useState, useEffect } from 'react';
import { Outlet } from '@tanstack/react-router';
import App from './App';
import Login from './pages/Login';
import { getActiveSession, endSession, type LocalSession } from './services/local-auth';

// Handle Atlassian OAuth2 callback in the popup window.
if (typeof window !== 'undefined' && window.location.pathname === '/oauth/callback') {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code && window.opener) {
    try {
      window.opener.postMessage(
        { type: 'atlassian-oauth2-callback', callbackUrl: window.location.href },
        window.location.origin
      );
    } catch (_) { /* opener may have navigated away */ }
    window.close();
  }
}

type AuthState = 'loading' | 'unauthenticated' | 'authenticated' | 'skipped' | 'migrate';

/** Detect a legacy token-based session that needs migration */
function getLegacyAuth(): { email: string } | null {
  try {
    const stored = localStorage.getItem('verigit-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      if ((parsed.email && parsed.token) || parsed.githubToken) {
        return { email: parsed.email || parsed.githubUsername || '' };
      }
    }
  } catch {}
  return null;
}

export function AppWrapper() {
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [session, setSession] = useState<LocalSession | null>(null);
  const [migrateEmail, setMigrateEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    const isElectron = window.location.protocol === 'app:' || navigator.userAgent.includes('Electron');
    document.documentElement.classList.toggle('is-electron', isElectron);
    return () => document.documentElement.classList.remove('is-electron');
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // 1. Check for a valid new session first
    const active = await getActiveSession();
    if (active) {
      setSession(active);
      setAuthState('authenticated');
      return;
    }
    // 2. Check for a legacy token session → prompt migration
    const legacy = getLegacyAuth();
    if (legacy) {
      setMigrateEmail(legacy.email);
      setAuthState('migrate');
      return;
    }
    // 2b. Check for saved profiles from the old quick-login system
    try {
      const profiles: Array<{ email?: string; githubUsername?: string }> = JSON.parse(
        localStorage.getItem('verigit-saved-profiles') || '[]'
      );
      if (profiles.length > 0) {
        const firstEmail = profiles[0].email || profiles[0].githubUsername || '';
        setMigrateEmail(firstEmail);
        setAuthState('migrate');
        return;
      }
    } catch { /* ignore */ }
    // 3. Check for a skip flag
    if (localStorage.getItem('verigit-skip-login') === 'true') {
      setAuthState('skipped');
      return;
    }
    setAuthState('unauthenticated');
  };

  const handleLogin = (newSession: LocalSession) => {
    setSession(newSession);
    setAuthState('authenticated');
  };

  const handleLogout = async () => {
    await endSession();
    setSession(null);
    localStorage.removeItem('verigit-skip-login');
    setAuthState('unauthenticated');
  };

  const handleSkipLogin = () => {
    localStorage.setItem('verigit-skip-login', 'true');
    setAuthState('skipped');
  };

  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <img src="/verigit.png" alt="VeriGit" className="h-16 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-400">Loading VeriGit...</p>
        </div>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return <Login onLogin={handleLogin} onSkip={handleSkipLogin} />;
  }

  if (authState === 'migrate') {
    return <Login onLogin={handleLogin} initialMode="migrate" migrateEmail={migrateEmail} />;
  }

  return (
    <>
      <App
        username={session?.userId ?? null}
        onLogout={handleLogout}
        storedUsername={session?.userId ?? null}
        storedEmail={session?.userId ?? null}
        storedToken={null}
        githubUsername={null}
        githubToken={null}
        onLogin={async () => {}}
      />
      <Outlet />
    </>
  );
}

// AppShell is the route component used by TanStack Router at /app
// It's the same as AppWrapper — just a named export for the router
export const AppShell = AppWrapper;
export default AppWrapper;
